import {
  IAdminConfiguration,
  ServiceTypes,
  convertToDecimals,
  fallbackText,
} from '@pharmaplan/common';
import ServiceHandler from '@pharmaplan/common/helpers/ServiceHandler';
import {
  Constants,
  nameLabels,
  telephoneInputMask,
  TextFieldVariants,
} from '../../../../helpers/Constants';
import strings from '../../../../localization';
import { IInputModalConfig } from '../../../Modals/InputModal/InputModal';
import styles from './styles';
import genericClasses from '../../../../theme/GenericClasses';

const keysToRemove = {
  pharmacyRate: 'pharmacyRate',
  pharmacistRate: 'pharmacistRate',
  techRate: 'techRate',
  salesForceKey: 'salesForceKey',
  ratePharmacy: 'ratePharmacy',
};

const {
  pharmacistRate: pharmacistRateKey,
  pharmacyRate: pharmacyRateKey,
  techRate: techRateKey,
  salesForceKey,
  ratePharmacy: ratePharmacyKey,
} = keysToRemove;

export const handleChip = (value: boolean) =>
  ({
    chipStyle: value ? styles.bgPositive : styles.bgNegative,
    value: value ? strings.yes : strings.no,
  });

export const settingsInfoConfig = (data: IAdminConfiguration) => {
  const isSelfService = ServiceHandler.getService() === ServiceTypes.self;

  const {
    ratePharmacistDays,
    ratePharmacyDays,
    minHourWorkshift,
    pharmacistRate,
    pharmacyRate,
    representativeName,
    representativeEmail,
    representativePhone,
    representativePhoneExt,
    salesForceActive,
    salesForcePassword,
    salesForceSecurityToken,
    salesForceURL,
    salesForceUserName,
    envoiceBCCAdmin,
    technicianRate,
    showBookingDetails,
    bookingStateExpiration,
    pharmacyReminderBefore,
    enableChat,
    basicPackagePrice,
    unlimitedPackagePrice,
    tpsPercentage,
    tvqPercentage,
    taxOnTax,
    tpsNumber,
    tvqNumber,
    companyName,
    address,
    phoneNumber,
    city,
    province,
  } = data ?? {};

  const title = isSelfService ? strings.pharmagap : strings.pharmaPlan;

  let commonContent = [
    {
      key: 0,
      label: strings.noOfDaysToRatePharmacist,
      value: ratePharmacistDays,
    },
    {
      key: ratePharmacyKey,
      label: strings.noOfDaysToRatePharmacy,
      value: ratePharmacyDays ?? '',
    },
    {
      key: 1,
      label: strings.minHoursWorkshift,
      value: minHourWorkshift,
    },
    {
      key: pharmacistRateKey,
      label: strings.formatString(
        strings.defaultHourlyRate,
        nameLabels().roleLabel,
      ) as string,
      value: `$${convertToDecimals(pharmacistRate)}`,
    },
    {
      key: pharmacyRateKey,
      label: strings.formatString(
        strings.defaultHourlyRate,
        strings.pharmacy,
      ) as string,
      value: `$${convertToDecimals(pharmacyRate)}`,
    },
    {
      key: techRateKey,
      label: strings.formatString(
        strings.defaultHourlyRate,
        `${strings.tech}.`,
      ) as string,
      value: `$${convertToDecimals(technicianRate)}`,
    },
  ];

  if (!isSelfService) {
    commonContent = commonContent.filter(
      (item) =>
        item.key !== ratePharmacyKey,
    );
  }

  const selfPreContent = [
    {
      key: '-1',
      label: strings.formatString(
        strings.feesFormat,
        strings.unlimited,
      ) as string,
      value: `$${convertToDecimals(unlimitedPackagePrice)}`,
    },
    {
      key: '-2',
      label: strings.formatString(
        strings.feesFormat,
        strings.standard,
      ) as string,
      value: `$${convertToDecimals(basicPackagePrice)}`,
    },
  ];

  const selfPostContent = [
    {
      key: '6',
      label: strings.requestPendingPeriod,
      value: `${bookingStateExpiration}${strings.hoursAbbreviation}`,
    },
    {
      key: '11',
      label: strings.pharmacyReminder,
      value: `${pharmacyReminderBefore}${strings.hoursAbbreviation}`,
    },
    {
      key: '12',
      label: strings.tps,
      value: `${tpsPercentage?.toFixed?.(3) ?? ''}%`,
    },
    {
      key: '13',
      label: strings.tvq,
      value: `${tvqPercentage?.toFixed?.(3) ?? ''}%`,
    },
    {
      key: '14',
      label: strings.taxOnTax,
      displayChip: true,
      ...handleChip(!!taxOnTax),
    },
  ];

  if (isSelfService) {
    commonContent.unshift(...selfPreContent);
    commonContent.push(...selfPostContent);
  }

  const pharmagapAccountSettings = {
    key: -1,
    title: strings.accountSettings,
    content: [
      {
        key: 0,
        label: strings.companyName,
        value: companyName ?? fallbackText,
      },
      {
        key: 1,
        label: strings.companyProvince,
        value: province ?? fallbackText,
      },
      {
        key: 2,
        label: strings.companyCity,
        value: city ?? fallbackText,
      },
      {
        key: 3,
        label: strings.companyAddress,
        value: address ?? fallbackText,
      },
      {
        key: 4,
        label: strings.companyPhone,
        value: phoneNumber ?? fallbackText,
      },
      isSelfService && {
        key: 5,
        label: strings.businessTPS,
        value: tpsNumber ?? fallbackText,
      },
      isSelfService && {
        key: 6,
        label: strings.businessTVQ,
        value: tvqNumber ?? fallbackText,
      },
    ].filter(Boolean) as Array<{
      key: number;
      label: string;
      value: string | number;
    }>,
  };

  let arr = [
    pharmagapAccountSettings,
    {
      key: 0,
      title: strings.formatString(strings.customSettings, title) as string,
      content: commonContent,
    },
    {
      key: 1,
      title: strings.formatString(
        strings.customSettings,
        strings.customerServiceRepresentative,
      ) as string,
      content: [
        {
          key: 0,
          label: strings.representativeName,
          value: representativeName,
        },
        {
          key: 1,
          label: strings.representativeEmail,
          value: representativeEmail,
        },
        {
          key: 2,
          label: strings.representativePhone,
          value: representativePhone,
        },
        {
          key: 3,
          label: strings.representativeExtPhone,
          value: representativePhoneExt,
        },
      ],
    },
    {
      key: salesForceKey,
      title: strings.formatString(
        strings.customSettings,
        strings.salesforce,
      ) as string,
      content: [
        {
          key: 0,
          label: strings.salesForceUsername,
          value: salesForceUserName,
        },
        {
          key: 1,
          label: strings.salesForcePWD,
          value: salesForcePassword,
          maskText: true,
        },
        {
          key: 2,
          label: strings.salesForceSecurityToken,
          value: salesForceSecurityToken,
          maskText: true,
        },
        {
          key: 3,
          label: strings.urlOfSalesForce,
          value: salesForceURL,
        },
        {
          key: 4,
          label: strings.integrationSalesForce,
          displayChip: true,
          ...handleChip(salesForceActive),
        },
      ],
    },
    {
      key: 2,
      title: strings.formatString(
        strings.customSettings,
        strings.more,
      ) as string,
      content: [
        {
          key: 0,
          label: strings.sendAllEmailsBCC,
          displayChip: true,
          ...handleChip(envoiceBCCAdmin),
        },
        {
          key: 1,
          label: strings.showAllBookingDetails,
          displayChip: true,
          ...handleChip(showBookingDetails),
        },
        {
          key: 2,
          label: strings.enableChat,
          displayChip: true,
          ...handleChip(enableChat),
        },
      ],
    },
  ];

  if (isSelfService) {
    arr = arr.filter((item) =>
      item.key !== salesForceKey);
  }

  return arr;
};

export const createNewForm: IInputModalConfig[] = [
  {
    key: '0',
    name: 'name',
    type: Constants.formInputType.text,
    variant: TextFieldVariants.filled,
    customTextFieldStyle: genericClasses.customFilledInput,
    gridLabel: strings.name,
    fieldXs: 8,
    labelXs: 4,
  },
  {
    key: '1',
    name: 'email',
    type: Constants.formInputType.text,
    variant: TextFieldVariants.filled,
    customTextFieldStyle: genericClasses.customFilledInput,
    gridLabel: strings.email,
    fieldXs: 8,
    labelXs: 4,
  },
  {
    key: '2',
    name: 'phone',
    gridLabel: strings.phone,
    variant: TextFieldVariants.filled,
    customTextFieldStyle: genericClasses.customFilledInput,
    fieldXs: 8,
    labelXs: 4,
    type: Constants.formInputType.masked,
    mask: telephoneInputMask,
    maxLength: 12,
  },
  {
    key: '3',
    name: 'received',
    type: Constants.formInputType.checkbox,
    gridLabel: strings.active,
    fieldXs: 8,
    labelXs: 4,
    customClass: genericClasses.checkboxContainer,
    customCheckboxStyle: genericClasses.checkboxStyle,
    gridLabelClass: styles.gridLabel,
  },
];
